import { MessageFiles } from '../../components/MessageFiles';
import { imageFromClick } from '../../lib/imageFromClick';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Clock3, FolderOpen, LoaderCircle } from 'lucide-react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import type { Attachment, StateUpdate } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { useChat } from '../../chat/ChatProvider';
import { Attachments, type AttachmentsHandle } from '../../components/Attachments';
import { ErrorState, LoadingState } from '../../components/AsyncState';
import { DateInput } from '../../components/DateInput';
import { LegacyStatusIcon } from '../../components/LegacyIcon';
import { Modal } from '../../components/Modal';
import { PersonCombobox } from '../../components/PersonCombobox';
import { RichTextEditor } from '../../components/RichTextEditor';
import { fullName } from '../../lib/reviewContext';
import { HistoryVideoReview } from '../../components/HistoryVideoReview';
import { MediaViewer } from '../../components/MediaViewer';
import { videoFromClick, videoReferenceHtml, type MediaSource } from '../../lib/mediaReview';
import { videoCommentAttachments, videoCommentGroups } from '../../lib/videoComments';
import { StatusBadge } from '../../components/StatusBadge';
import { STATE_TYPES, formatDate, formatHours, fromDateInput, stateName, toDateInput } from '../../lib/status';
import { renderRichText, stripInlineAttachments } from '../../lib/richText';
import type { MessageAttachment } from '../../lib/messageAttachments';
import { formatStateDateRange, stateDatesChanged } from './stateNotification';

interface FormState {
  type: string;
  executorId: string;
  examinerId: string;
  startDate: string;
  endDate: string;
  planHours: string;
  factHours: string;
  comment: string;
}

const EMPTY_FORM: FormState = {
  type: '',
  executorId: '',
  examinerId: '',
  startDate: '',
  endDate: '',
  planHours: '',
  factHours: '',
  comment: '',
};

const optionalNumber = (value: string) => (value === '' ? null : Number(value));

interface RelatedStateDecision {
  fix: boolean | undefined;
  comment: string;
}

const escapeHtml = (value: string) => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;');

const HistoryComment = memo(function HistoryComment({ comment, files }: { comment: string; files?: readonly MessageAttachment[] }) {
  return <div className="rich-content" dangerouslySetInnerHTML={{ __html: renderRichText(comment, files) }} />;
});

export function StateEditorModal({
  stateId,
  onClose,
}: {
  stateId: number | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const chat = useChat();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [relatedDecisions, setRelatedDecisions] = useState<Record<number, RelatedStateDecision>>({});
  const [datesValid, setDatesValid] = useState({ start: true, end: true });
  const [uploading, setUploading] = useState(false);
  const attachmentControl = useRef<AttachmentsHandle>(null);
  const [media, setMedia] = useState<MediaSource | null>(null);

  const stateQuery = useQuery({
    queryKey: ['state', stateId],
    queryFn: () => api.state(stateId!),
    enabled: stateId !== null,
  });
  const projectId = stateQuery.data?.asset?.project?.id;
  const usersQuery = useQuery({
    queryKey: ['users', 'project', projectId],
    queryFn: () => api.users({ projectId, addInfo:true }),
    enabled: projectId !== undefined,
  });
  const pendingAttachmentsQuery = useQuery({
    queryKey: ['state-attachments', stateId],
    queryFn: () => api.pendingAttachments(stateId!),
    enabled: stateId !== null,
  });

  useEffect(() => {
    const state = stateQuery.data;
    if (!state) return;
    setForm({
      type: state.type?.id ?? '',
      executorId: state.executor ? String(state.executor.id) : '',
      examinerId: state.examiner ? String(state.examiner.id) : '',
      startDate: toDateInput(state.start_date),
      endDate: toDateInput(state.end_date),
      planHours: state.plan_hours == null ? '' : String(state.plan_hours),
      factHours: state.fact_hours == null ? '' : String(state.fact_hours),
      comment: '',
    });
    setRelatedDecisions({});
    setDatesValid({ start: true, end: true });
  }, [stateQuery.data]);

  useEffect(() => {
    setAttachments(pendingAttachmentsQuery.data ?? []);
  }, [pendingAttachmentsQuery.data]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const state = stateQuery.data!;
      const startDate = fromDateInput(form.startDate);
      const endDate = fromDateInput(form.endDate);
      const payload: StateUpdate = {
        type: form.type || null,
        executor_id: optionalNumber(form.executorId),
        examiner_id: optionalNumber(form.examinerId),
        start_date: startDate,
        end_date: endDate,
        plan_hours: optionalNumber(form.planHours),
        fact_hours: optionalNumber(form.factHours),
        comment: form.comment || null,
        file_paths: form.type === 'готово' ? state.file_paths ?? {} : {},
      };
      const updated = await api.updateState(state.id, payload);
      const nextNotifications: Array<{ recipientIds: Array<number | undefined>; message: string }> = [];
      if (form.type === 'исправить') {
        await Promise.all(
          (state.next_states ?? [])
            .filter((next) => relatedDecisions[next.id]?.fix || relatedDecisions[next.id]?.comment.trim())
            .map(async (next) => {
              const decision = relatedDecisions[next.id];
              const comment = decision.comment.trim() || `В этапе «${state.regulation_step?.title ?? ''}» внесены изменения, поэтому после их выполнения этот этап тоже нужно поправить.`;
              await api.updateState(next.id, {
                ...(decision.fix ? { type: 'исправить' } : {}),
                comment,
              });
              const nextTitle = next.regulation_step?.title ?? 'Следующий этап';
              const nextLink = state.asset?.project && state.asset.regulation
                ? `${window.location.origin}/projects/${state.asset.project.id}/assets/${state.asset.regulation.id}?ids=${state.asset.id}&rstep=${next.regulation_step?.id ?? ''}`
                : window.location.href;
              nextNotifications.push({
                recipientIds: [next.executor?.id, next.examiner?.id],
                message: `<a href="${nextLink}">${escapeHtml(`${state.asset?.title ?? 'Ассет'} · ${nextTitle} · ${state.asset?.regulation?.title ?? ''}`)}</a><br>${decision.fix ? 'Изменилось состояние — исправить' : 'Добавлен комментарий'}<br>${escapeHtml(comment)}`,
              });
            }),
        );
      }

      const projectUsers = usersQuery.data ?? await api.users({ projectId });
      const activeRecipients = new Set(projectUsers.filter((item) => item.active).map((item) => item.id));
      const currentUserId = user?.id;
      const sendTo = (recipientIds: Array<number | null | undefined>, message: string, files?: Attachment[]) => {
        [...new Set(recipientIds)]
          .filter((id): id is number => Boolean(id && id !== currentUserId && activeRecipients.has(id)))
          .forEach((id) => chat.sendDirect(id, message, undefined, files));
      };
      const getUserName = (id: number | null) => projectUsers.find((item) => item.id === id)?.initial ?? 'Не задан';
      const newExecutorId = optionalNumber(form.executorId);
      const newExaminerId = optionalNumber(form.examinerId);
      const oldExecutorId = state.executor?.id ?? null;
      const oldExaminerId = state.examiner?.id ?? null;
      const link = state.asset?.project && state.asset.regulation
        ? `${window.location.origin}/projects/${state.asset.project.id}/assets/${state.asset.regulation.id}?ids=${state.asset.id}&rstep=${state.regulation_step?.id ?? ''}`
        : window.location.href;
      const changes: string[] = [];
      if (oldExecutorId !== newExecutorId) changes.push(`Новый исполнитель — ${escapeHtml(getUserName(newExecutorId))}`);
      if (oldExaminerId !== newExaminerId) changes.push(`Новый рецензент — ${escapeHtml(getUserName(newExaminerId))}`);
      if (stateDatesChanged(state.start_date, state.end_date, form.startDate, form.endDate)) {
        changes.push(`Изменились сроки: ${formatStateDateRange(form.startDate, form.endDate)}`);
      }
      if (state.plan_hours !== optionalNumber(form.planHours) || state.fact_hours !== optionalNumber(form.factHours)) {
        changes.push(`Изменились часы: план ${formatHours(optionalNumber(form.planHours))}, факт ${formatHours(optionalNumber(form.factHours))}`);
      }
      if ((state.type?.id ?? '') !== form.type) {
        changes.push(`Изменилось состояние — ${escapeHtml(STATE_TYPES.find((item) => item.text === form.type)?.text ?? 'пусто')}`);
      }
      const heading = escapeHtml(`${state.asset?.title ?? 'Ассет'} · ${state.regulation_step?.title ?? ''} · ${state.asset?.regulation?.title ?? ''}`);
      const primaryMessage = [`<a href="${link}">${heading}</a>`, ...changes, form.comment].filter(Boolean).join('<br>');
      const primaryRecipients = [newExecutorId, newExaminerId];
      if (oldExecutorId !== newExecutorId) primaryRecipients.push(oldExecutorId);
      if (oldExaminerId !== newExaminerId) primaryRecipients.push(oldExaminerId);
      sendTo(primaryRecipients, primaryMessage, attachments);
      nextNotifications.forEach((notification) => sendTo(notification.recipientIds, notification.message));
      return updated;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['states'] }),
        queryClient.invalidateQueries({ queryKey: ['assets'] }),
        queryClient.invalidateQueries({ queryKey: ['state', stateId] }),
        queryClient.invalidateQueries({ queryKey: ['state-attachments', stateId] }),
      ]);
      onClose();
    },
  });

  const showRelated = useMemo(
    () => form.type === 'исправить' && stateQuery.data?.type?.id !== 'исправить',
    [form.type, stateQuery.data?.type?.id],
  );
  const relatedDecisionsComplete = !showRelated || !(stateQuery.data?.next_states?.length)
    || stateQuery.data.next_states.every((next) => relatedDecisions[next.id]?.fix !== undefined);

  if (stateId === null) return null;

  const state = stateQuery.data;
  const frames = state?.asset?.extra?.frames;
  const title = state
    ? <span className="task-card-title">
        <span className="task-card-title__hierarchy">
          {[
            { kind: 'project', text: state.asset?.project?.title },
            { kind: 'regulation', text: state.asset?.regulation?.title },
            { kind: 'step', text: state.regulation_step?.title },
          ].filter(item => item.text).map((item, index) => <span className={`task-card-title__${item.kind}`} key={item.kind} title={item.text}>
            {index > 0 && <span className="task-card-title__separator" aria-hidden="true">›</span>}
            <span className="task-card-title__name">{item.text}</span>
          </span>)}
        </span>
        <span className="task-card-title__asset-line">
          <span className="task-card-title__asset" title={state.asset?.title}>{state.asset?.title || 'Ассет'}</span>
          {frames != null && <span className="task-card-title__frames" title="Количество кадров">({frames})</span>}
        </span>
      </span>
    : 'Задача';

  return (
    <>
    <Modal
      onClose={onClose}
      className="state-editor-modal telegram-task-card"
      size="large"
      title={title}
    >
      {stateQuery.isPending ? <LoadingState label="Загружаем задачу…" /> : null}
      {stateQuery.error ? <ErrorState error={stateQuery.error} retry={() => void stateQuery.refetch()} /> : null}
      {state ? (
        <div className="state-editor">
          <section className="state-editor__form">
            <div className="status-picker" role="radiogroup" aria-label="Статус">
              {[
                ...STATE_TYPES.map((type) => ({ ...type, value: type.text })),
                { name: 'empty' as const, text: 'пусто', value: '', active: false },
              ].map((type) => (
                <label className={`status-choice status-choice--${type.name}`} key={type.name}>
                  <input
                    checked={form.type === type.value}
                    name="state-type"
                    onChange={() => {
                      setForm((current) => ({ ...current, type: type.value }));
                      if (type.value === 'исправить') {
                        setRelatedDecisions((current) => Object.fromEntries(
                          (state.next_states ?? []).map((next) => [
                            next.id,
                            current[next.id] ?? { fix: next.type === null ? false : undefined, comment: '' },
                          ]),
                        ));
                      }
                    }}
                    type="radio"
                  />
                  <LegacyStatusIcon large name={type.name} off={form.type !== type.value} />
                  <span>{type.text}</span>
                </label>
              ))}
            </div>

            {showRelated && state.next_states?.length ? (
              <div className="related-states">
                <h3>Исправить следующие этапы</h3>
                <p className="muted">Для каждого этапа выберите действие. Комментарий можно отправить и без смены состояния.</p>
                {state.next_states.map((next) => (
                  <div className="related-state-row" key={next.id}>
                    <div className="related-state-row__stage">
                      <StatusBadge compact state={next} />
                      <strong>{next.regulation_step?.title}</strong>
                    </div>
                    <label>
                      <input
                        checked={relatedDecisions[next.id]?.fix === true}
                        name={`related-state-${next.id}`}
                        onChange={() => setRelatedDecisions((current) => ({
                          ...current,
                          [next.id]: { ...(current[next.id] ?? { comment: '' }), fix: true },
                        }))}
                        type="radio"
                      />
                      Исправить
                    </label>
                    <label>
                      <input
                        checked={relatedDecisions[next.id]?.fix === false}
                        name={`related-state-${next.id}`}
                        onChange={() => setRelatedDecisions((current) => ({
                          ...current,
                          [next.id]: { ...(current[next.id] ?? { comment: '' }), fix: false },
                        }))}
                        type="radio"
                      />
                      Не исправлять
                    </label>
                    <textarea
                      aria-label={`Комментарий для этапа ${next.regulation_step?.title ?? ''}`}
                      onChange={(event) => setRelatedDecisions((current) => ({
                        ...current,
                        [next.id]: { ...(current[next.id] ?? { fix: undefined }), comment: event.target.value },
                      }))}
                      placeholder="Комментарий"
                      rows={2}
                      value={relatedDecisions[next.id]?.comment ?? ''}
                    />
                  </div>
                ))}
              </div>
            ) : null}

            <div className="state-editor__fields">
              <div className="state-editor__assignees">
                <PersonCombobox
                  label="Исполнитель"
                  nullLabel="Не назначен"
                  onChange={(executorId) => setForm((current) => ({ ...current, executorId }))}
                  users={usersQuery.data ?? []}
                  value={form.executorId}
                />
                <PersonCombobox
                  label="Ответственный"
                  nullLabel="Не назначен"
                  onChange={(examinerId) => setForm((current) => ({ ...current, examinerId }))}
                  users={usersQuery.data ?? []}
                  value={form.examinerId}
                />
              </div>
              <div className="state-editor__schedule">
                <div className="state-pair-row">
                  <strong><CalendarDays size={14} /> Сроки</strong>
                  <label className="field">
                    <span>Начало</span>
                    <DateInput
                      ariaLabel="Начало"
                      onChange={(startDate) => setForm((current) => ({ ...current, startDate }))}
                      onValidChange={(start) => setDatesValid((current) => ({ ...current, start }))}
                      value={form.startDate}
                    />
                  </label>
                  <label className="field">
                    <span>Окончание</span>
                    <DateInput
                      ariaLabel="Окончание"
                      onChange={(endDate) => setForm((current) => ({ ...current, endDate }))}
                      onValidChange={(end) => setDatesValid((current) => ({ ...current, end }))}
                      value={form.endDate}
                    />
                  </label>
                </div>
                <div className="state-pair-row">
                  <strong><Clock3 size={14} /> Часы</strong>
                  <label className="field">
                    <span>План</span>
                    <input disabled={!state.regulation_step?.display_hours || !(user?.admin || user?.supervisor)} min="0" step="0.5" type="number" value={form.planHours} onChange={(event) => setForm((current) => ({ ...current, planHours: event.target.value }))} />
                  </label>
                  <label className="field">
                    <span>Факт</span>
                    <input disabled={!state.regulation_step?.display_hours} min="0" step="0.5" type="number" value={form.factHours} onChange={(event) => setForm((current) => ({ ...current, factHours: event.target.value }))} />
                  </label>
                </div>
              </div>
            </div>

            {state.file_paths && Object.keys(state.file_paths).length ? (
              <div className="path-list compact-list">
                <strong><FolderOpen size={16} /> Пути</strong>
                {Object.entries(state.file_paths).map(([name, path]) => (
                  <a href={path.startsWith('https://') ? path : `smp:${path}`} key={name} rel="noreferrer" target="_blank">{name}</a>
                ))}
              </div>
            ) : null}

            <RichTextEditor
              reviewContext={{author:fullName(user),recipient:fullName(usersQuery.data?.find(person => person.id === Number(form.executorId))),project:state?.asset?.project?.title,regulation:state?.asset?.regulation?.title,stage:state?.regulation_step?.title,asset:state?.asset?.title}}
              minHeight={82}
              variant="messenger"
              disabled={updateMutation.isPending}
              onChange={(comment) => setForm((current) => ({ ...current, comment }))}
              onFiles={(files) => { void attachmentControl.current?.addFiles(files); }}
              onImageUpload={async (file) => (await attachmentControl.current?.addFiles([file]))?.[0] ?? null}
              onImageReplace={async (id, file) => { if (!attachmentControl.current) throw new Error('Редактор закрыт'); return attachmentControl.current.replaceFile(id, file); }}
              onVideoUpload={async (file) => (await attachmentControl.current?.addFiles([file]))?.[0] ?? null}
              placeholder="Комментарий к изменению"
              value={form.comment}
              onAttachmentRemove={async ids => { if (!attachmentControl.current) throw new Error('Редактор закрыт'); await attachmentControl.current.removeFiles(ids); }}
            />
            <Attachments
              hiddenIds={videoCommentAttachments(form.comment)}
              groups={videoCommentGroups(form.comment)}
              attachments={attachments}
              onChange={(next, replacedId) => {
                const removed = attachments.filter((item) => item.id !== replacedId && !next.some((kept) => kept.id === item.id)).map((item) => item.id);
                if (removed.length) setForm((current) => ({ ...current, comment: stripInlineAttachments(current.comment, removed) }));
                setAttachments(next);
              }}
              onUploadingChange={setUploading}
              ref={attachmentControl}
              showTrigger={false}
              stateId={state.id}
            />
            <div className="state-editor__actions">
              {!datesValid.start || !datesValid.end ? <span className="form-error">Введите дату в формате дд.мм.гггг</span> : null}
              {!relatedDecisionsComplete ? <span className="form-error">Выберите действие для каждого следующего этапа</span> : null}
              {updateMutation.error ? <span className="form-error">{updateMutation.error.message}</span> : null}
              <button
                className="button button--primary"
                disabled={updateMutation.isPending || uploading || !datesValid.start || !datesValid.end || !relatedDecisionsComplete}
                onClick={() => updateMutation.mutate()}
                type="button"
              >
                {updateMutation.isPending ? <LoaderCircle className="spin" size={15} /> : null}
                Применить
              </button>
            </div>
          </section>

          <aside data-video-state-id={state.id} className="state-history" onClick={event => { const source = videoFromClick(event.target) || imageFromClick(event.target); if (source) { event.preventDefault(); setMedia(source); } }}>
            <h3>История <span>{state.logs?.length ?? 0}</span></h3>
            {state.logs?.length ? state.logs.map((log) => (
              <article className="history-item" key={log.id}>
                <div className="history-item__state">
                  <span className={`history-state status--${stateName(log.type?.text)}`}>
                    <LegacyStatusIcon name={stateName(log.type?.text)} />
                    <strong>{log.type?.text ?? 'пусто'}</strong>
                  </span>
                  <dl className="history-item__people">
                    <div>
                      <dt>Исполнитель</dt>
                      <dd>{log.executor?.initial ?? 'Не назначен'}</dd>
                    </div>
                    <div>
                      <dt>Ответственный</dt>
                      <dd>{log.examiner?.initial ?? 'Не назначен'}</dd>
                    </div>
                  </dl>
                </div>
                <div className="history-item__content">
                  <div className="history-item__meta">
                    <time>{formatDate(log.added, true)}</time>
                    <span>Изменил: <b>{log.owner?.initial ?? '—'}</b></span>
                  </div>
                  {log.comment ? <HistoryComment comment={log.comment} files={log.attachments} /> : null}
                  <div className="history-item__properties">
                    {(log.start_date || log.end_date) ? <span><b>Сроки</b> {formatDate(log.start_date)} — {formatDate(log.end_date)}</span> : null}
                    {(log.plan_hours != null || log.fact_hours != null) ? <span><b>Часы, план / факт</b> {formatHours(log.plan_hours)} / {formatHours(log.fact_hours)}</span> : null}
                  </div>
                  {(log.attachments?.length || (log.file_paths && Object.keys(log.file_paths).length)) ? (
                    <div className="compact-list history-item__files">
                      {Object.entries(log.file_paths ?? {}).map(([name, path]) => (
                        <a href={path.startsWith('https://') ? path : `smp:${path}`} key={name} rel="noreferrer" target="_blank">{name}</a>
                      ))}
                      <MessageFiles files={log.attachments} html={log.comment} voiceContext={{logId:log.id}}/>
                    </div>
                  ) : null}
                </div>
              </article>
            )) : <p className="muted">История пока пуста</p>}
          </aside>
        </div>
      ) : null}
    </Modal>
    {media?.kind === 'video' && <HistoryVideoReview source={{...media,reviewContext:{author:fullName(user),recipient:fullName(usersQuery.data?.find(person => person.id === Number(form.executorId))),project:state?.asset?.project?.title,regulation:state?.asset?.regulation?.title,stage:state?.regulation_step?.title,asset:state?.asset?.title}}} stateId={stateId} draftKey={`state:${user?.id}:${stateId}`} canSend={!updateMutation.isPending} onClose={() => setMedia(null)} onSend={(html,_files,owned) => {setForm(current => ({...current,comment:current.comment+html}));setAttachments(current => [...current,...owned.filter(file => !current.some(item => item.id === file.id))]);}}/>}
    {media && media.kind !== 'video' && <MediaViewer source={media} onClose={() => setMedia(null)} onAnnotated={async (file, reference) => { const uploaded = (await attachmentControl.current?.addFiles([file]))?.[0]; if (!uploaded) throw new Error("Не удалось загрузить картинку с правками"); if (reference) setForm(current => ({ ...current, comment: current.comment + videoReferenceHtml(reference) })); }}/>}
    </>
  );
}
